import { normalizeDescription, stripDiacritics } from './normalize.js';
/**
 * Motor de regras de categorizacao.
 *
 * Puro de proposito: entra um lancamento e uma lista de regras, sai a
 * categoria sugerida. Sem banco, sem I/O — assim a regra "posto de gasolina e
 * Transporte / Combustivel" pode ser testada sem subir nada.
 *
 * O motor NUNCA decide sozinho o que vai para o banco. Ele sugere, e a tela de
 * conferencia mostra a sugestao antes de qualquer gravacao: categorizar errado
 * em silencio estraga o relatorio do mes sem deixar rastro.
 */
export class RuleError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RuleError';
    }
}
/**
 * COUNTERPARTY existe porque a descricao nem sempre carrega a identidade.
 *
 * Num PIX o extrato diz "Pix enviado" e ponto: quem recebeu vem noutro campo.
 * Uma regra ancorada so na descricao nunca alcancaria esses lancamentos — e
 * sao justamente eles os que mais se repetem todo mes.
 */
export const RULE_FIELDS = ['DESCRIPTION', 'COUNTERPARTY', 'AMOUNT', 'ACCOUNT'];
export const RULE_OPERATORS = [
    'CONTAINS',
    'EQUALS',
    'STARTS_WITH',
    'ENDS_WITH',
    'REGEX',
    'GREATER_THAN',
    'LESS_THAN',
    'BETWEEN',
];
/** Primeira regra habilitada que casa por inteiro, na ordem de prioridade. */
export function matchRule(candidate, rules) {
    const ordered = [...rules]
        .filter((rule) => rule.enabled)
        // Empate na prioridade e resolvido pelo id, para o resultado nao depender
        // da ordem em que o banco devolveu as linhas.
        .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
    for (const rule of ordered) {
        if (rule.conditions.length === 0)
            continue;
        if (rule.kind && kindOf(candidate.amountCents) !== rule.kind)
            continue;
        if (!rule.conditions.every((condition) => matchCondition(candidate, condition)))
            continue;
        return {
            ruleId: rule.id,
            ruleName: rule.name,
            categoryId: rule.categoryId,
            tagIds: rule.tagIds,
        };
    }
    return null;
}
function kindOf(amountCents) {
    return amountCents >= 0n ? 'INCOME' : 'EXPENSE';
}
export function matchCondition(candidate, condition) {
    if (condition.field === 'AMOUNT')
        return matchAmount(candidate.amountCents, condition);
    const haystack = textoDoCampo(candidate, condition.field);
    const needle = condition.value === null ? '' : comparableText(condition.value);
    if (needle === '' && condition.operator !== 'REGEX')
        return false;
    switch (condition.operator) {
        case 'CONTAINS':
            return haystack.includes(needle);
        case 'EQUALS':
            return haystack === needle;
        case 'STARTS_WITH':
            return haystack.startsWith(needle);
        case 'ENDS_WITH':
            return haystack.endsWith(needle);
        case 'REGEX':
            return matchRegex(haystack, condition.value ?? '');
        default:
            // Operador numerico em campo de texto e configuracao invalida, nao um
            // casamento falso: barrar aqui evita uma regra que nunca dispara e
            // ninguem entende por que.
            throw new RuleError(`O operador ${condition.operator} nao vale para o campo ${condition.field}.`);
    }
}
/**
 * O texto contra o qual a condicao e comparada.
 *
 * A descricao passa pela forma normalizada — quem escreve "posto ipiranga"
 * precisa acertar "COMPRA CARTAO POSTO IPIRANGA 0412 384756". O nome da
 * contraparte NAO passa: ele ja chega limpo, e normalizar de novo removeria
 * numero que faz parte do nome ("Posto 24 Horas").
 *
 * Contraparte ausente vira string vazia, que nunca casa com agulha nao vazia.
 * Uma regra de contraparte simplesmente nao alcanca quem ainda nao tem nome —
 * em vez de alcancar todo mundo, que seria o jeito perigoso de errar.
 */
function textoDoCampo(candidate, field) {
    if (field === 'ACCOUNT')
        return comparableText(candidate.accountName);
    if (field === 'COUNTERPARTY')
        return comparableText(candidate.counterpartyName ?? '');
    return normalizeDescription(candidate.description);
}
function comparableText(value) {
    return stripDiacritics(value).toUpperCase().replace(/\s+/g, ' ').trim();
}
/**
 * Expressao regular do usuario, com duas protecoes:
 *
 *  - compila sob demanda e devolve `false` se o padrao for invalido, em vez de
 *    derrubar a importacao inteira por causa de um parenteses solto;
 *  - limita o tamanho do padrao. Nao resolve ReDoS, so reduz a superficie —
 *    por isso o padrao tambem passa por `assertSafeRegex` ao ser gravado.
 */
function matchRegex(haystack, pattern) {
    try {
        return new RegExp(pattern, 'iu').test(haystack);
    }
    catch {
        return false;
    }
}
function matchAmount(amountCents, condition) {
    // O usuario pensa em magnitude ("acima de R$ 500"), nao em sinal.
    const value = amountCents < 0n ? -amountCents : amountCents;
    const min = condition.amountMinCents;
    const max = condition.amountMaxCents;
    switch (condition.operator) {
        case 'GREATER_THAN':
            if (min === null)
                throw new RuleError('A regra de valor minimo esta sem o valor.');
            return value > min;
        case 'LESS_THAN':
            if (max === null)
                throw new RuleError('A regra de valor maximo esta sem o valor.');
            return value < max;
        case 'BETWEEN':
            if (min === null || max === null) {
                throw new RuleError('A regra de faixa precisa do valor minimo e do maximo.');
            }
            return value >= min && value <= max;
        case 'EQUALS':
            if (min === null)
                throw new RuleError('A regra de valor exato esta sem o valor.');
            return value === min;
        default:
            throw new RuleError(`O operador ${condition.operator} nao vale para o campo AMOUNT.`);
    }
}
/**
 * Recusa padroes com quantificador aninhado — o gatilho classico de
 * backtracking catastrofico, tipo `(a+)+$`. Um usuario nao vai escrever isso
 * de proposito, mas um padrao colado da internet derrubaria a importacao de
 * todo mundo que divide o household.
 */
export function assertSafeRegex(pattern) {
    if (pattern.length > 200) {
        throw new RuleError('A expressao e longa demais (limite de 200 caracteres).');
    }
    try {
        new RegExp(pattern, 'iu');
    }
    catch {
        throw new RuleError('A expressao regular nao e valida.');
    }
    if (/(\([^)]*[+*][^)]*\)|\[[^\]]*\][^\s]*)\s*[+*]/.test(pattern)) {
        throw new RuleError('Esta expressao tem repeticao dentro de repeticao, que pode travar a importacao. Simplifique o padrao.');
    }
}
//# sourceMappingURL=rules.js.map